import { DocumentNode } from "@apollo/client";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { ReactNode, useEffect, useRef, useState } from "react";
import { useLocaleState, useTranslate } from "react-admin";
import { Link as RouterLink } from "react-router-dom";

import { BILLING_LIST_PAGE_SIZE } from "../../constants/pagination";
import {
  AccountBillingAvailability,
  AccountBillingDocument,
  AccountBillingInvoicesDocument,
  AccountBillingInvoicesQuery,
  AccountBillingLedgerDocument,
  AccountBillingLedgerQuery,
  AccountBillingProfileDocument,
  AccountBillingProfileQuery,
  AccountBillingQuery,
  AccountBillingSubscriptionsDocument,
  AccountBillingSubscriptionsQuery,
  CreateAccountBillingInvoiceDownloadDocument,
  CreateAccountBillingInvoiceDownloadMutation,
  CreateAccountBillingInvoiceDownloadMutationVariables,
  CrmObject,
} from "../../generated/graphql";
import { useBillingInvoiceDownload } from "../../hooks/useBillingInvoiceDownload";
import { client } from "../../lib/apolloClient";
import { CRM_COLORS, crmRecordPath, formatCrmDateTime } from "../../lib/crm";
import { captureException } from "../../lib/errorHandling";
import { trace } from "../../lib/trace";
import { ListPagination } from "../ListPagination";
import { CrmCard } from "./CrmCard";
import { CrmFieldList, CrmSection } from "./CrmSection";

const INVENTORY_POLL_INTERVAL_MS = 3000;
const INVENTORY_POLL_LIMIT = 10;
type BillingIdentity = { salesforceId: string; mappingKey: string };
const BILLING_SECTIONS = ["invoices", "profile", "subscriptions", "ledger"] as const;
type BillingSection = (typeof BILLING_SECTIONS)[number];
type BillingRoot = {
  availability: AccountBillingAvailability;
  matchingReason?: string | null;
  billedThrough?: { salesforceId: string } | null;
  customer?: { id: string } | null;
  invoices?: unknown;
  profile?: unknown;
  subscriptions?: unknown;
  ledger?: unknown;
};
const billingMappingKey = (billing: BillingRoot) =>
  JSON.stringify([
    billing.customer?.id ?? null,
    billing.billedThrough?.salesforceId ?? null,
    billing.matchingReason ?? null,
  ]);
type BillingResult = { customerServiceAccountBilling: BillingRoot };
type BillingSeed<T extends BillingResult = AccountBillingQuery> = {
  data: T;
  failedSections: BillingSection[];
};
type Variables = { salesforceId: string; after?: string | null; dateFrom?: string; dateTo?: string };

// Every section resolves account authority again. A mapping change clears the old view.
const useBillingRead = <T extends BillingResult>(
  document: DocumentNode,
  variables: Variables,
  onMappingChange?: (availability: AccountBillingAvailability) => void,
  mappingKey?: string,
  section?: BillingSection,
  initial?: BillingSeed<T>
) => {
  const [state, setState] = useState<{ status: "loading" | "error" } | ({ status: "ready" } & BillingSeed<T>)>(() =>
    initial
      ? section &&
        (initial.data.customerServiceAccountBilling[section] == null || initial.failedSections.includes(section))
        ? { status: "error" }
        : { status: "ready", ...initial }
      : { status: "loading" }
  );
  const [revision, setRevision] = useState(0);
  const variablesKey = JSON.stringify(variables);
  const initialVariables = useRef(variablesKey);
  const seedEligible = useRef(Boolean(initial));
  useEffect(() => {
    // Keep the root snapshot only for the original mount, including StrictMode effect replay.
    // Once any page, filter or retry changes the request, returning to page one reads afresh.
    if (seedEligible.current && revision === 0 && variablesKey === initialVariables.current) return;
    seedEligible.current = false;
    let active = true;
    setState({ status: "loading" });
    trace("ui", "billing.load", {}, "loading");
    const load = async () => {
      try {
        const result = await client.query<T>({
          query: document,
          variables: JSON.parse(variablesKey),
          fetchPolicy: "no-cache",
          errorPolicy: "all",
          context: { queryDeduplication: false },
        });
        if (!active) return;
        const billing = result.data?.customerServiceAccountBilling;
        if (!billing) throw new Error("Missing account billing data");
        if (
          mappingKey &&
          (billingMappingKey(billing) !== mappingKey || billing.availability !== AccountBillingAvailability.Available)
        ) {
          setState({ status: "error" });
          trace("ui", "billing.load", {}, "mapping_changed");
          onMappingChange?.(billing.availability);
          return;
        }
        const failedSections = BILLING_SECTIONS.filter(key => result.errors?.some(error => error.path?.[1] === key));
        if (section && (billing[section] == null || failedSections.includes(section))) {
          setState({ status: "error" });
          trace("ui", "billing.load", {}, "error");
          return;
        }
        trace("ui", "billing.load", {}, "ready");
        setState({ status: "ready", data: result.data as T, failedSections });
      } catch (error) {
        captureException(error, "crm/account-billing: load section");
        if (active) {
          trace("ui", "billing.load", {}, "error");
          setState({ status: "error" });
        }
      }
    };
    void load();
    return () => {
      active = false;
    };
  }, [document, variablesKey, revision, mappingKey, onMappingChange, section]);
  return {
    data: state.status === "ready" ? state.data : undefined,
    failedSections: state.status === "ready" ? state.failedSections : [],
    loading: state.status === "loading",
    error: state.status === "error",
    retry: () => {
      setState({ status: "loading" });
      setRevision(value => value + 1);
    },
  };
};

const ReadState = ({ loading, error, retry }: { loading: boolean; error: boolean; retry: () => void }) => {
  const translate = useTranslate();
  if (loading) return <CircularProgress size={20} aria-label={translate("crm.accountBilling.loading")} />;
  if (error)
    return (
      <Alert severity="error" action={<Button onClick={retry}>{translate("billing.retry")}</Button>}>
        {translate("billing.loadError")}
      </Alert>
    );
  return null;
};

const useBillingFormat = () => {
  const [locale] = useLocaleState();
  const translate = useTranslate();
  const missing = translate("account.notAvailable");
  const date = (value?: string | null) =>
    value ? new Intl.DateTimeFormat(locale, { timeZone: "UTC" }).format(new Date(value)) : missing;
  const money = (value?: string | null, currency?: string | null) => {
    if (value == null) return missing;
    if (!currency || !Number.isFinite(Number(value))) return `${value} ${currency ?? ""}`.trim();
    try {
      return new Intl.NumberFormat(locale, { style: "currency", currency }).format(Number(value));
    } catch {
      return `${value} ${currency}`;
    }
  };
  const boolean = (value?: boolean | null) =>
    value == null ? missing : translate(value ? "crm.record.yes" : "crm.record.no");
  const providerValue = (
    group:
      | "lifecycleStatus"
      | "deliveryMethod"
      | "ledgerType"
      | "paymentStatus"
      | "paymentTransactionStatus"
      | "billingPeriodUnit",
    value?: string | null,
    count?: number | null
  ) =>
    value == null
      ? missing
      : translate(`crm.accountBilling.values.${group}.${value}`, { _: value, smart_count: count ?? 1 });
  const label = (key: string) => translate(`crm.accountBilling.${key}`);
  const fields = (values: Record<string, ReactNode>) =>
    Object.entries(values).map(([key, value]) => ({ key, label: label(key), value: value ?? missing }));
  return { date, money, boolean, providerValue, label, fields, missing };
};

const BillingTable = ({ title, headings, children }: { title: string; headings: string[]; children: ReactNode }) => (
  <TableContainer>
    <Table
      size="small"
      aria-label={title}
      sx={{
        "& th, & td": { fontSize: "13px", px: "12px", py: "8px", borderColor: CRM_COLORS.cardBorder },
        "& thead": { backgroundColor: CRM_COLORS.pageBackground },
        "& th": { color: CRM_COLORS.label },
      }}
    >
      <TableHead>
        <TableRow>
          {headings.map(heading => (
            <TableCell key={heading}>{heading}</TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>{children}</TableBody>
    </Table>
  </TableContainer>
);

const useCursors = () => {
  const [cursors, setCursors] = useState<Array<string | null>>([null]);
  const after = cursors[cursors.length - 1];
  return {
    after,
    page: cursors.length,
    back: () => setCursors(value => (value.length > 1 ? value.slice(0, -1) : value)),
    change: (page: number, next?: string | null) =>
      setCursors(value => (page < value.length ? value.slice(0, page) : next ? [...value, next] : value)),
  };
};

const Paging = ({
  cursor,
  next,
  loading,
}: {
  cursor: ReturnType<typeof useCursors>;
  next?: string | null;
  loading: boolean;
}) => (
  <ListPagination
    resource="crm"
    page={cursor.page}
    perPage={BILLING_LIST_PAGE_SIZE}
    hasNextPage={Boolean(next)}
    isPending={loading}
    onPageChange={page => cursor.change(page, next)}
  />
);

type SectionProps = BillingIdentity & {
  onMappingChange: (availability: AccountBillingAvailability) => void;
  initial?: BillingSeed;
};

const InvoicePage = ({
  salesforceId,
  mappingKey,
  dateFrom,
  dateTo,
  initial,
  onMappingChange,
}: SectionProps & { dateFrom: string; dateTo: string }) => {
  const translate = useTranslate();
  const { date, money, label, missing } = useBillingFormat();
  const cursor = useCursors();
  const read = useBillingRead<AccountBillingInvoicesQuery>(
    AccountBillingInvoicesDocument,
    { salesforceId, after: cursor.after, dateFrom: dateFrom || undefined, dateTo: dateTo || undefined },
    onMappingChange,
    mappingKey,
    "invoices",
    initial
  );
  const page = read.data?.customerServiceAccountBilling.invoices;
  const { downloadingId, downloadError, readyDownload, download } = useBillingInvoiceDownload(async invoiceId => {
    // Only visible rows can initiate a download; the server also rechecks account ownership.
    if (!page?.items.some(item => item.id === invoiceId)) return null;
    const result = await client.mutate<
      CreateAccountBillingInvoiceDownloadMutation,
      CreateAccountBillingInvoiceDownloadMutationVariables
    >({
      mutation: CreateAccountBillingInvoiceDownloadDocument,
      variables: { salesforceId, invoiceId },
      fetchPolicy: "no-cache",
    });
    return result.data?.createAccountBillingInvoiceDownload;
  });
  useEffect(() => {
    if (cursor.after && page?.items.length === 0) cursor.back();
  }, [cursor.after, page]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <>
      <ReadState {...read} />
      {page &&
        (page.items.length ? (
          <BillingTable
            title={label("invoices")}
            headings={[label("number"), label("date"), label("dueDate"), label("amount"), label("document")]}
          >
            {page.items.map(invoice => (
              <TableRow key={invoice.id}>
                <TableCell>{invoice.number ?? missing}</TableCell>
                <TableCell>{date(invoice.documentDate)}</TableCell>
                <TableCell>{date(invoice.dueDate)}</TableCell>
                <TableCell sx={{ whiteSpace: "nowrap" }}>{money(invoice.totalGross, invoice.currency)}</TableCell>
                <TableCell>
                  <Button
                    size="small"
                    disabled={downloadingId !== null}
                    startIcon={<DownloadOutlinedIcon />}
                    aria-label={translate("billing.downloadLabel", { number: invoice.number ?? invoice.documentDate })}
                    onClick={() => void download(invoice.id)}
                  >
                    {translate(downloadingId === invoice.id ? "billing.downloading" : "billing.download")}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </BillingTable>
        ) : (
          <Typography>{translate("billing.empty")}</Typography>
        ))}
      {downloadError && <Alert severity="error">{translate("billing.downloadError")}</Alert>}
      {readyDownload && (
        <Alert severity="info">
          {translate("billing.popupBlocked")}{" "}
          <Link href={readyDownload.url} target="_blank" rel="noopener noreferrer" referrerPolicy="no-referrer">
            {translate("billing.openDocument")}
          </Link>
        </Alert>
      )}
      <Paging cursor={cursor} next={page?.nextCursor} loading={read.loading || downloadingId !== null} />
    </>
  );
};

const Invoices = (props: SectionProps) => {
  const { label } = useBillingFormat();
  const [filter, setFilter] = useState({ dateFrom: "", dateTo: "" });
  const initial = useRef(props.initial);
  const valid = !filter.dateFrom || !filter.dateTo || filter.dateFrom <= filter.dateTo;
  return (
    <CrmCard title={label("invoices")} testId="account-billing-invoices">
      <Stack direction="row" gap={2} flexWrap="wrap" mb={2}>
        {(["dateFrom", "dateTo"] as const).map(key => (
          <TextField
            key={key}
            type="date"
            size="small"
            label={label(key)}
            InputLabelProps={{ shrink: true }}
            value={filter[key]}
            onChange={event => {
              initial.current = undefined;
              setFilter(value => ({ ...value, [key]: event.target.value }));
            }}
          />
        ))}
      </Stack>
      {valid ? (
        <InvoicePage key={`${filter.dateFrom}:${filter.dateTo}`} {...props} {...filter} initial={initial.current} />
      ) : (
        <Alert severity="warning">{label("invalidDates")}</Alert>
      )}
    </CrmCard>
  );
};

const Profile = ({ salesforceId, mappingKey, onMappingChange, initial }: SectionProps) => {
  const { fields, label, providerValue } = useBillingFormat();
  const read = useBillingRead<AccountBillingProfileQuery>(
    AccountBillingProfileDocument,
    { salesforceId },
    onMappingChange,
    mappingKey,
    "profile",
    initial
  );
  const profile = read.data?.customerServiceAccountBilling.profile;
  const address = profile?.address;
  return (
    <CrmSection id="account-billing-profile" title={label("profile")}>
      <ReadState {...read} />
      {profile && (
        <CrmFieldList
          items={fields({
            displayName: profile.displayName,
            externalCustomerId: profile.externalCustomerId,
            debtorAccount: profile.debtorAccount,
            email: profile.email,
            phone: profile.phone,
            vatId: profile.vatId,
            buyerReference: profile.buyerReference,
            deliveryMethod: providerValue("deliveryMethod", profile.deliveryMethod),
            address: address
              ? [
                  address.addressLine1,
                  address.addressLine2,
                  [address.street, address.houseNumber].filter(Boolean).join(" "),
                  [address.postalCode, address.city].filter(Boolean).join(" "),
                  address.state,
                  address.country,
                ]
                  .filter(Boolean)
                  .join(", ") || null
              : null,
          })}
        />
      )}
    </CrmSection>
  );
};

const Subscriptions = ({ salesforceId, mappingKey, onMappingChange, initial }: SectionProps) => {
  const { fields, label, date, money, boolean, providerValue } = useBillingFormat();
  const cursor = useCursors();
  const read = useBillingRead<AccountBillingSubscriptionsQuery>(
    AccountBillingSubscriptionsDocument,
    { salesforceId, after: cursor.after },
    onMappingChange,
    mappingKey,
    "subscriptions",
    initial
  );
  const page = read.data?.customerServiceAccountBilling.subscriptions;
  useEffect(() => {
    if (cursor.after && page?.items.length === 0) cursor.back();
  }, [cursor.after, page]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <CrmSection
      id="account-billing-subscriptions"
      title={label("subscriptions")}
      testId="account-billing-subscriptions"
    >
      <ReadState {...read} />
      {page?.items.map(item => (
        <Box key={item.id} sx={{ mb: "12px" }}>
          <CrmFieldList
            items={fields({
              contractId: item.id,
              planName: item.planName,
              planVariantId: item.planVariantId,
              lifecycleStatus: providerValue("lifecycleStatus", item.lifecycleStatus),
              billingPeriod: item.billingPeriod
                ? [
                    item.billingPeriod.quantity,
                    item.billingPeriod.unit == null
                      ? null
                      : providerValue("billingPeriodUnit", item.billingPeriod.unit, item.billingPeriod.quantity),
                  ]
                    .filter(value => value != null)
                    .join(" ") || null
                : null,
              startDate: date(item.startDate),
              endDate: date(item.endDate),
              lastBillingDate: date(item.lastBillingDate),
              nextBillingDate: date(item.nextBillingDate),
              billedUntil: date(item.billedUntil),
              balance: money(item.balance, item.currency),
              paymentMethod: item.paymentMethod,
              billingSuspended: boolean(item.billingSuspended),
              recurringPaymentsPaused: boolean(item.recurringPaymentsPaused),
              dunningLevel: item.dunning?.level,
              dunningRemaining: money(item.dunning?.remaining, item.currency),
              dunningSince: date(item.dunning?.since),
            })}
          />
        </Box>
      ))}
      {page?.items.length === 0 && <Typography>{label("noData")}</Typography>}
      <Paging cursor={cursor} next={page?.nextCursor} loading={read.loading} />
    </CrmSection>
  );
};

const Ledger = ({ salesforceId, mappingKey, onMappingChange, initial }: SectionProps) => {
  const { label, money, missing, providerValue } = useBillingFormat();
  const cursor = useCursors();
  const read = useBillingRead<AccountBillingLedgerQuery>(
    AccountBillingLedgerDocument,
    { salesforceId, after: cursor.after },
    onMappingChange,
    mappingKey,
    "ledger",
    initial
  );
  const page = read.data?.customerServiceAccountBilling.ledger;
  useEffect(() => {
    if (cursor.after && page?.items.length === 0) cursor.back();
  }, [cursor.after, page]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <CrmSection id="account-billing-ledger" title={label("ledger")} testId="account-billing-ledger">
      <ReadState {...read} />
      {page &&
        (page.items.length ? (
          <BillingTable
            title={label("ledger")}
            headings={[
              label("date"),
              label("type"),
              label("description"),
              label("amount"),
              label("paymentStatus"),
              label("paymentTransactionStatus"),
              label("contractId"),
              label("invoiceId"),
            ]}
          >
            {page.items.map(item => (
              <TableRow key={item.id}>
                <TableCell>{item.timestamp ? formatCrmDateTime(item.timestamp) : missing}</TableCell>
                <TableCell>{providerValue("ledgerType", item.type)}</TableCell>
                <TableCell>{item.description ?? missing}</TableCell>
                <TableCell>{money(item.amount, item.currency)}</TableCell>
                <TableCell>{providerValue("paymentStatus", item.paymentStatus)}</TableCell>
                <TableCell>{providerValue("paymentTransactionStatus", item.paymentTransactionStatus)}</TableCell>
                <TableCell>{item.contractId ?? missing}</TableCell>
                <TableCell>{item.invoiceId ?? missing}</TableCell>
              </TableRow>
            ))}
          </BillingTable>
        ) : (
          <Typography>{label("noData")}</Typography>
        ))}
      <Paging cursor={cursor} next={page?.nextCursor} loading={read.loading} />
    </CrmSection>
  );
};

const AccountBillingContent = ({ salesforceId }: { salesforceId: string }) => {
  const { fields, label } = useBillingFormat();
  const read = useBillingRead<AccountBillingQuery>(AccountBillingDocument, { salesforceId });
  const billing = read.data?.customerServiceAccountBilling;
  const [polls, setPolls] = useState(0);
  useEffect(() => {
    if (billing?.availability !== AccountBillingAvailability.Loading || polls >= INVENTORY_POLL_LIMIT) return;
    const timer = window.setTimeout(() => {
      setPolls(value => value + 1);
      read.retry();
    }, INVENTORY_POLL_INTERVAL_MS);
    return () => window.clearTimeout(timer);
  }, [billing, polls]); // eslint-disable-line react-hooks/exhaustive-deps
  // Stable across section requests; remount the whole account view on a changed mapping.
  const [mappingChanged, setMappingChanged] = useState<AccountBillingAvailability | null>(null);
  const [onMappingChange] = useState(
    () => (availability: AccountBillingAvailability) => setMappingChanged(availability)
  );
  if (mappingChanged)
    return (
      <Alert
        severity="warning"
        action={
          <Button
            onClick={() => {
              setMappingChanged(null);
              read.retry();
            }}
          >
            {label("retry")}
          </Button>
        }
      >
        {label(
          mappingChanged === AccountBillingAvailability.Available ? "mappingChanged" : `availability.${mappingChanged}`
        )}
      </Alert>
    );
  return (
    <Stack gap="12px" data-testid="account-billing">
      <ReadState {...read} />
      {billing && (
        <>
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              onClick={() => {
                setPolls(0);
                read.retry();
              }}
            >
              {label("refresh")}
            </Button>
          </Box>
          {billing.availability !== AccountBillingAvailability.Available ? (
            <Alert
              severity={billing.availability === AccountBillingAvailability.MappingConflict ? "warning" : "info"}
              action={
                billing.availability !== AccountBillingAvailability.NotConfigured &&
                billing.availability !== AccountBillingAvailability.IntegrationNotConfigured ? (
                  <Button
                    onClick={() => {
                      setPolls(0);
                      read.retry();
                    }}
                  >
                    {label("retry")}
                  </Button>
                ) : undefined
              }
            >
              {label(`availability.${billing.availability}`)}
            </Alert>
          ) : (
            billing.customer && (
              <>
                <CrmFieldList
                  items={fields({
                    billedThrough: billing.billedThrough ? (
                      <Link
                        component={RouterLink}
                        to={crmRecordPath(CrmObject.Account, billing.billedThrough.salesforceId)}
                      >
                        {billing.billedThrough.name ?? billing.billedThrough.salesforceId}
                      </Link>
                    ) : null,
                    customer: (
                      <Link
                        href={billing.customer.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        referrerPolicy="no-referrer"
                      >
                        {billing.customer.displayName ?? billing.customer.externalCustomerId ?? billing.customer.id}
                      </Link>
                    ),
                    externalCustomerId: billing.customer.externalCustomerId,
                    debtorAccount: billing.customer.debtorAccount,
                  })}
                />
                <Invoices
                  salesforceId={salesforceId}
                  mappingKey={billingMappingKey(billing)}
                  onMappingChange={onMappingChange}
                  initial={{ data: read.data!, failedSections: read.failedSections }}
                />
                <Profile
                  salesforceId={salesforceId}
                  mappingKey={billingMappingKey(billing)}
                  onMappingChange={onMappingChange}
                  initial={{ data: read.data!, failedSections: read.failedSections }}
                />
                <Subscriptions
                  salesforceId={salesforceId}
                  mappingKey={billingMappingKey(billing)}
                  onMappingChange={onMappingChange}
                  initial={{ data: read.data!, failedSections: read.failedSections }}
                />
                <Ledger
                  salesforceId={salesforceId}
                  mappingKey={billingMappingKey(billing)}
                  onMappingChange={onMappingChange}
                  initial={{ data: read.data!, failedSections: read.failedSections }}
                />
              </>
            )
          )}
        </>
      )}
    </Stack>
  );
};

export const AccountBilling = ({ salesforceId, deleted = false }: { salesforceId: string; deleted?: boolean }) => {
  const translate = useTranslate();
  return deleted ? (
    <Alert severity="info">{translate("crm.accountBilling.deleted")}</Alert>
  ) : (
    <AccountBillingContent key={salesforceId} salesforceId={salesforceId} />
  );
};
